/* eslint-disable max-lines */
import { DocumentSubtitleV1 } from '@utils/types/editors/subtitles/subtitle.ts';

const STORAGE_VERSION = '1.0';
const STORAGE_PREFIX = 'caption-editor';
const MAX_HISTORY_SIZE = 20; // Reduced from 200 to save localStorage space
const CLEANUP_THRESHOLD_DAYS = 7;
// Very large documents (multi-hour transcripts, tens of thousands of cues)
// serialize well past the ~5MB localStorage quota, so every autosave would throw
// QuotaExceededError and thrash cleanup/retry — and the full JSON.stringify +
// re-parse spikes mobile memory (Den Haag 11h incident). Skip local draft
// persistence past this size; server sync still protects the work.
const DRAFT_MAX_SEGMENTS = 5000;

export interface CaptionStorageData {
  version: string;
  sessionId: string;
  documentId: string;
  document: DocumentSubtitleV1;
  undoStack: DocumentSubtitleV1[];
  redoStack: DocumentSubtitleV1[];
  lastLocalSave: number; // timestamp
  lastServerSync: number | null; // timestamp
  isDirty: boolean;
  filename?: string;
  timecodeOffset?: string;
}

export interface CaptionStorageMetadata {
  sessionId: string;
  documentId: string;
  lastLocalSave: number;
  lastServerSync: number | null;
}

/**
 * Generate storage key for a caption document
 */
function getStorageKey(sessionId: string, documentId: string): string {
  return `${STORAGE_PREFIX}-${sessionId}-${documentId}`;
}

/**
 * Generate metadata key for a caption document
 */
function getMetadataKey(sessionId: string, documentId: string): string {
  return `${STORAGE_PREFIX}-meta-${sessionId}-${documentId}`;
}

/**
 * Check if localStorage is available
 */
export function isLocalStorageAvailable(): boolean {
  try {
    const test = '__localStorage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Save caption document data to localStorage
 */
export function saveCaptionToLocalStorage(
  sessionId: string,
  documentId: string,
  data: Partial<CaptionStorageData>
): boolean {
  if (!isLocalStorageAvailable()) {
    return false;
  }

  // Skip persisting oversized documents entirely — cheap length check before any
  // parse/stringify. Guarding on data.document means the first large save is
  // skipped, so nothing large ever lands in localStorage and later merge-saves
  // stay cheap too.
  if ((data.document?.document?.length ?? 0) > DRAFT_MAX_SEGMENTS) {
    return false;
  }

  try {
    const key = getStorageKey(sessionId, documentId);
    const metaKey = getMetadataKey(sessionId, documentId);

    // Load existing data to merge
    const existing = loadCaptionFromLocalStorage(sessionId, documentId);

    const defaultDocument: DocumentSubtitleV1 = {
      document: [],
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      document_type: 'SubtitleV1' as any,
      version: '1'
    };

    const storageData: CaptionStorageData = {
      version: STORAGE_VERSION,
      sessionId,
      documentId,
      document: data.document ?? existing?.document ?? defaultDocument,
      // Don't save undo/redo stacks to localStorage to save space - they're only needed in memory
      undoStack: [],
      redoStack: [],
      lastLocalSave:
        data.lastLocalSave ?? existing?.lastLocalSave ?? Date.now(),
      lastServerSync: data.lastServerSync ?? existing?.lastServerSync ?? null,
      isDirty: data.isDirty ?? existing?.isDirty ?? true,
      filename: data.filename ?? existing?.filename,
      timecodeOffset: data.timecodeOffset ?? existing?.timecodeOffset
    };

    // Trim undo/redo stacks to max size
    if (storageData.undoStack.length > MAX_HISTORY_SIZE) {
      storageData.undoStack = storageData.undoStack.slice(-MAX_HISTORY_SIZE);
    }
    if (storageData.redoStack.length > MAX_HISTORY_SIZE) {
      storageData.redoStack = storageData.redoStack.slice(-MAX_HISTORY_SIZE);
    }

    // Save metadata separately for quick lookups
    const metadata: CaptionStorageMetadata = {
      sessionId,
      documentId,
      lastLocalSave: storageData.lastLocalSave,
      lastServerSync: storageData.lastServerSync
    };

    localStorage.setItem(key, JSON.stringify(storageData));
    localStorage.setItem(metaKey, JSON.stringify(metadata));

    return true;
  } catch (error) {
    if (error instanceof Error && error.name === 'QuotaExceededError') {
      cleanupOldEntries();

      // Try one more time after cleanup
      try {
        const key = getStorageKey(sessionId, documentId);
        const defaultDocument: DocumentSubtitleV1 = {
          document: [],
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          document_type: 'SubtitleV1' as any,
          version: '1'
        };

        const storageData: CaptionStorageData = {
          version: STORAGE_VERSION,
          sessionId,
          documentId,
          document: data.document ?? defaultDocument,
          // Don't save undo/redo stacks to localStorage to save space
          undoStack: [],
          redoStack: [],
          lastLocalSave: Date.now(),
          lastServerSync: data.lastServerSync ?? null,
          isDirty: data.isDirty ?? true,
          filename: data.filename,
          timecodeOffset: data.timecodeOffset
        };
        localStorage.setItem(key, JSON.stringify(storageData));
        return true;
      } catch (retryError) {
        return false;
      }
    }
    return false;
  }
}

/**
 * Load caption document data from localStorage
 */
export function loadCaptionFromLocalStorage(
  sessionId: string,
  documentId: string
): CaptionStorageData | null {
  if (!isLocalStorageAvailable()) {
    return null;
  }

  try {
    const key = getStorageKey(sessionId, documentId);
    const data = localStorage.getItem(key);

    if (!data) {
      return null;
    }

    const parsed: CaptionStorageData = JSON.parse(data);

    // Validate version
    if (parsed.version !== STORAGE_VERSION) {
      // Could implement migration logic here in the future
    }

    return parsed;
  } catch (error) {
    // If data is corrupted, remove it
    try {
      const key = getStorageKey(sessionId, documentId);
      localStorage.removeItem(key);
    } catch (e) {
      // Ignore cleanup errors
    }
    return null;
  }
}

/**
 * Get metadata for a caption document
 */
export function getCaptionMetadata(
  sessionId: string,
  documentId: string
): CaptionStorageMetadata | null {
  if (!isLocalStorageAvailable()) {
    return null;
  }

  try {
    const metaKey = getMetadataKey(sessionId, documentId);
    const data = localStorage.getItem(metaKey);

    if (!data) {
      return null;
    }

    return JSON.parse(data);
  } catch (error) {
    return null;
  }
}

/**
 * Clear caption document data from localStorage
 */
export function clearCaptionFromLocalStorage(
  sessionId: string,
  documentId: string
): void {
  if (!isLocalStorageAvailable()) {
    return;
  }

  try {
    const key = getStorageKey(sessionId, documentId);
    const metaKey = getMetadataKey(sessionId, documentId);
    localStorage.removeItem(key);
    localStorage.removeItem(metaKey);
  } catch (error) {
    // Error clearing localStorage
  }
}

/**
 * Save undo/redo stacks to localStorage (deprecated - no longer saves to localStorage)
 * Undo/redo stacks are now kept only in memory to save localStorage space
 */
export function saveUndoRedoStacks(
  _sessionId: string,
  _documentId: string,
  _undoStack: DocumentSubtitleV1[],
  _redoStack: DocumentSubtitleV1[]
): boolean {
  // No longer save undo/redo stacks to localStorage
  return true;
}

/**
 * Mark document as synced with server
 */
export function markAsSynced(sessionId: string, documentId: string): boolean {
  return saveCaptionToLocalStorage(sessionId, documentId, {
    lastServerSync: Date.now(),
    isDirty: false
  });
}

/**
 * Cleanup old localStorage entries
 */
export function cleanupOldEntries(): void {
  if (!isLocalStorageAvailable()) {
    return;
  }

  try {
    const now = Date.now();
    const thresholdMs = CLEANUP_THRESHOLD_DAYS * 24 * 60 * 60 * 1000;
    const keysToRemove: string[] = [];

    // Find all caption editor keys
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(STORAGE_PREFIX)) {
        continue;
      }

      // Check if it's a metadata key
      if (key.startsWith(`${STORAGE_PREFIX}-meta-`)) {
        try {
          const data = localStorage.getItem(key);
          if (data) {
            const metadata: CaptionStorageMetadata = JSON.parse(data);
            const age = now - metadata.lastLocalSave;

            if (age > thresholdMs) {
              // Mark both metadata and data keys for removal
              keysToRemove.push(key);
              const dataKey = key.replace(
                `${STORAGE_PREFIX}-meta-`,
                `${STORAGE_PREFIX}-`
              );
              keysToRemove.push(dataKey);
            }
          }
        } catch (e) {
          // If we can't parse it, it's probably corrupted, so remove it
          keysToRemove.push(key);
        }
      }
    }

    // Remove old entries
    keysToRemove.forEach(key => {
      try {
        localStorage.removeItem(key);
      } catch (e) {
        // Failed to remove key
      }
    });
  } catch (error) {
    // noop
  }
}

/**
 * Get all caption documents in localStorage
 */
export function getAllCaptionDocuments(): CaptionStorageMetadata[] {
  if (!isLocalStorageAvailable()) {
    return [];
  }

  const documents: CaptionStorageMetadata[] = [];

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(`${STORAGE_PREFIX}-meta-`)) {
        continue;
      }

      try {
        const data = localStorage.getItem(key);
        if (data) {
          const metadata: CaptionStorageMetadata = JSON.parse(data);
          documents.push(metadata);
        }
      } catch (e) {
        // Skip corrupted entries
        continue;
      }
    }
  } catch (error) {
    // Error getting all caption documents
  }

  return documents;
}

/**
 * Get storage usage estimate in bytes
 */
export function getStorageUsageEstimate(): number {
  if (!isLocalStorageAvailable()) {
    return 0;
  }

  let total = 0;
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(STORAGE_PREFIX)) {
        const value = localStorage.getItem(key);
        if (value) {
          total += key.length + value.length;
        }
      }
    }
  } catch (error) {
    // Error calculating storage usage
  }

  return total * 2; // Each character is ~2 bytes in UTF-16
}
